import { useMemo } from 'react';

import cx from 'classnames';
import type { ControlProps, GroupBase } from 'react-select-5';
import ReactSelect from 'react-select-5';
import ReactSelectAsync from 'react-select-5/async';
import ReactSelectAsyncCreatable from 'react-select-5/async-creatable';
import ReactSelectCreatable from 'react-select-5/creatable';

import { getBrowser } from 'utils/common';

import s from './base.module.css';
import type { LabelProp, SelectProps } from './base.types';
import { ClearIndicator } from './components/ClearIndicator';
import { Container } from './components/Container';
import { Control } from './components/Control';
import { CreateOption } from './components/CreateOption';
import { DropdownIndicator } from './components/DropdownIndicator';
import { Label } from './components/Label';
import { Menu } from './components/Menu';
import { MultiValueRemove } from './components/MultiValueRemove';
import { MultiValueContainer } from './components/Sortable/MultiValueContainer';
import { MultiValueLabel } from './components/Sortable/MultiValueLabel';
import { ValueContainer } from './components/Sortable/ValueContainer';

function LabeledControl<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(label?: LabelProp<Option, IsMulti, Group>) {
  return (p: ControlProps<Option, IsMulti, Group>) => (
    <Control<Option, IsMulti, Group>
      {...p}
      label={label && <Label<Option, IsMulti, Group> {...p}>{label?.(p)}</Label>}
    />
  );
}

const components = {
  base: ReactSelect,
  async: ReactSelectAsync,
  creatable: ReactSelectCreatable,
  asyncCreatable: ReactSelectAsyncCreatable,
};
/**
 * A wrapper around react-select@5 that only customizes the look of its components.
 */
export function Select<
  _Option = unknown,
  _IsMulti extends boolean = false,
  _Group extends GroupBase<_Option> = GroupBase<_Option>,
>(props: SelectProps<_Option, _IsMulti, _Group>): JSX.Element {
  const labelIsSet = !!props.label;
  const isMenuAnimationEnabled = getBrowser().name !== 'Safari' || parseInt(getBrowser().version) >= 18;

  // https://github.com/JedWatson/react-select/issues/3833#issuecomment-741891654
  const formatOptionLabel = (...args: Parameters<NonNullable<(typeof props)['formatOptionLabel']>>) => {
    const [opt, parameters] = args;
    const { context } = parameters;
    const option = opt as { __isNew__?: boolean; label: any };
    // If there's no getOptionLabel provided, React Select uses "label" prop by default
    const label = props.getOptionLabel?.(option as _Option) || option.label;

    return context === 'menu' && option.__isNew__ ? <CreateOption value={label} /> : label;
  };

  const Control = useMemo(() => LabeledControl<_Option, _IsMulti, _Group>(props.label), [props.label]);

  const componentType = useMemo(() => {
    if (props.creatable && props.async) return 'asyncCreatable';
    if (props.creatable) return 'creatable';
    if (props.async) return 'async';
    return 'base';
  }, [props.creatable, props.async]);

  const Component = useMemo(() => components[componentType], [componentType]);

  return (
    <Component<_Option, _IsMulti, _Group>
      formatCreateLabel={(v) => v}
      formatOptionLabel={formatOptionLabel}
      isDisabled={props.readOnly}
      {...props}
      ref={props.selectRef}
      components={{
        ...props.components,
        SelectContainer: props.components?.SelectContainer ?? Container,
        Control: props.components?.Control ?? Control,
        ClearIndicator: props.components?.ClearIndicator ?? ClearIndicator,
        DropdownIndicator: props.components?.DropdownIndicator ?? DropdownIndicator,
        Menu: props.components?.Menu ?? Menu,
        MultiValueRemove: props.components?.MultiValueRemove ?? MultiValueRemove,
        ...(props.sortable
          ? {
              MultiValueContainer: props.components?.MultiValueContainer ?? MultiValueContainer,
              MultiValueLabel: props.components?.MultiValueLabel ?? MultiValueLabel,
              ValueContainer: props.components?.ValueContainer ?? ValueContainer,
            }
          : {}),
      }}
      classNames={{
        ...props.classNames,
        container: (p) => cx(s.baseContainer, s.container, props.classNames?.container?.(p)),
        control: (p) =>
          cx(
            s.baseControl,
            s.control,
            {
              [s.focused]: p.isFocused,
              [s.disabled]: p.isDisabled,
            },
            props.classNames?.control?.(p),
          ),
        placeholder: (p) =>
          cx(
            s.placeholder,
            { [s.withLabel]: labelIsSet, [s.focused]: p.isFocused },
            props.classNames?.placeholder?.(p),
          ),
        singleValue: (p) => cx(s.singleValue, props.classNames?.singleValue?.(p)),
        valueContainer: (p) =>
          cx(
            s.valueContainer,
            {
              [s.withLabel]: labelIsSet,
              [s.isMulti]: p.isMulti,
              [s.filled]: p.hasValue,
              [s.clearable]: p.selectProps.isClearable,
            },
            props.classNames?.valueContainer?.(p),
          ),
        input: (p) => cx(s.input, props.classNames?.input?.(p)),
        indicatorsContainer: (p) => cx(s.indicatorsContainer, props.classNames?.indicatorsContainer?.(p)),
        indicatorSeparator: (p) => cx(s.indicatorSeparator, props.classNames?.indicatorSeparator?.(p)),
        dropdownIndicator: (p) =>
          cx(s.dropdownIndicator, { [s.open]: p.selectProps.menuIsOpen }, props.classNames?.dropdownIndicator?.(p)),
        clearIndicator: (p) => cx(s.clearIndicator, props.classNames?.clearIndicator?.(p)),
        menu: (p) => cx(s.baseMenu, s.menu, { [s.menuAnim]: isMenuAnimationEnabled }, props.classNames?.menu?.(p)),
        menuList: (p) => cx(s.menuList, props.classNames?.menuList?.(p)),
        option: (p) =>
          cx(
            s.baseOption,
            s.option,
            { [s.focused]: p.isFocused, [s.disabled]: p.isDisabled },
            props.classNames?.option?.(p),
          ),
        groupHeading: (p) => cx(s.groupHeading, props.classNames?.groupHeading?.(p)),
        multiValue: (p) => cx(s.multiValue, props.classNames?.multiValue?.(p)),
        multiValueLabel: (p) => cx(s.multiValueLabel, props.classNames?.multiValueLabel?.(p)),
        multiValueRemove: (p) => cx(s.multiValueRemove, props.classNames?.multiValueRemove?.(p)),
        noOptionsMessage: (p) => cx(s.noOptionsMessage, props.classNames?.noOptionsMessage?.(p)),
        loadingMessage: (p) => cx(s.loadingMessage, props.classNames?.loadingMessage?.(p)),
      }}
      styles={{
        ...props.styles,
        container: (base, cProps) => ({
          ...props.styles?.container?.(base, cProps),
        }),
        // react-select's unstyled prop does not remove some styles
        // so we need to remove the base styles here
        // and reapply the only ones we need with s.base* classes
        control: (base, cProps) => ({
          ...props.styles?.control?.(base, cProps),
        }),
        valueContainer: (base, cProps) => ({
          ...props.styles?.valueContainer?.(base, cProps),
        }),
        dropdownIndicator: (base, cProps) => ({
          ...props.styles?.dropdownIndicator?.(base, cProps),
        }),
        menu: (base, cProps) => ({
          ...props.styles?.menu?.(base, cProps),
        }),
        option: (base, cProps) => ({
          ...props.styles?.option?.(base, cProps),
        }),
      }}
      unstyled
    />
  );
}
